import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { clearLocalStorage, setLocal } from '../../helper/storage'
import { axiosInstance } from '../../axios/interceptor'

export const userRegistration = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/client/auth/register', data)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const userLogin = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/client/auth/login', data)
    if (response?.data) {
      setLocal('authToken', response?.data?.token)
      setLocal('refrteshToken', response?.data?.refreshToken)
      setLocal('isTermAccepted', false)
    }
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const userGoogleLogin = createAsyncThunk('auth/google', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/client/auth/google', data)
    if (response?.data) {
      setLocal('authToken', response?.data?.token)
      setLocal('refrteshToken', response?.data?.refreshToken)
      setLocal('isTermAccepted', response?.data?.data?.is_term_accepted)
    }
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const userFacebookLogin = createAsyncThunk('auth/facebook', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/client/auth/facebook', data)
    if (response?.data) {
      setLocal('authToken', response?.data?.token)
      setLocal('refrteshToken', response?.data?.refreshToken)
      setLocal('isTermAccepted', response?.data?.data?.is_term_accepted)
    }
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const userLogout = createAsyncThunk('auth/logout', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get('/logout')
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getUser = createAsyncThunk('auth/me', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get('/client/auth/me')
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/client/auth/forgot-password`, data)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const resetPassword = createAsyncThunk('auth/resetPassword', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/client/auth/update-password`, data)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const verifyEmail = createAsyncThunk('auth/verifyEmail', async (token, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/client/auth/verify-client-email?token=${token}`)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const resendVerificationEmail = createAsyncThunk('auth/resendEmail', async (email, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/client/auth/resend-email-verification-link/${email}`)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const AcceptsTermsAndConditions = createAsyncThunk('auth/termAndCondition', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/client/auth/accept-terms-and-conditions`)
    setLocal('isTermAccepted', response?.data?.data?.is_term_accepted)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const updateProfile = createAsyncThunk('auth/updateProfile', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(`/client/auth/update-profile`, data)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const changePassword = createAsyncThunk('auth/changePassword', async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/client/auth/change-password`, data)
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
})

const initialState = {
  isLoading: false,
  isSocialLogin: false,
  user: null,
  error: null
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.user = null
      clearLocalStorage()
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(userRegistration.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(userRegistration.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(userRegistration.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(userLogin.pending, (state) => {
        state.isLoading = true
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload?.data?.data
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(userGoogleLogin.pending, (state) => {
        state.isLoading = true
      })
      .addCase(userGoogleLogin.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload?.data?.data
        state.isSocialLogin = true
      })
      .addCase(userGoogleLogin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(userFacebookLogin.pending, (state) => {
        state.isLoading = true
      })
      .addCase(userFacebookLogin.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload?.data?.data
        state.isSocialLogin = true
      })
      .addCase(userFacebookLogin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(userLogout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(userLogout.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(userLogout.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(getUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.user = action.payload?.data?.data
        state.isLoading = false
      })
      .addCase(getUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(resendVerificationEmail.pending, (state) => {
        state.isLoading = true
      })
      .addCase(resendVerificationEmail.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(AcceptsTermsAndConditions.pending, (state) => {
        state.isLoading = true
      })
      .addCase(AcceptsTermsAndConditions.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(AcceptsTermsAndConditions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload?.data?.data
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
  }
})

export const { resetAuthState } = authSlice.actions
export default authSlice.reducer
